import { defineStore } from 'pinia'
import { ref, computed, watch } from 'vue'
import { cloneDeep } from 'lodash'
import api from '@app/insights/api'
import I18n from '@app/adapters/I18n'
import SFetch from '@app/utils/sfetch'
import { isResponseError } from '@app/utils/predicates'
import { useSegmentsStore } from './segments'

interface TriggerAction {
  action: string
  payload: {
    text: string
    todo?: boolean
    tag_ids?: number[]
    [key: string]: unknown
  }
}

interface Trigger {
  id?: number
  title: string
  frequency: string
  running?: boolean
  finished?: boolean
  total_count?: number
  processed_ids?: number[]
  trigger_action: TriggerAction
  segment: { id: number; name?: string }
  last_run_at?: string
  next_run_on?: string
}

interface TriggerSaveParams {
  title: string
  frequency: string
  insight_trigger_action_attributes: {
    action: string
    payload: TriggerAction['payload']
  }
}

// draftKey is the batch-stable identity: the id for persisted triggers, a
// negative counter otherwise. params is absent until an edit stages a change.
interface DraftTrigger extends Trigger {
  draftKey: number
  params?: TriggerSaveParams
}

interface TriggerRunningStatus {
  id: number
  running: boolean
  finished?: boolean
  total_count?: number
  processed_ids?: number[]
}

const NO_REPEAT_TRIGGER_FREQUENCIES = {
  MANUAL: 'manual',
  ONE_OFF: 'one_off',
}

export const useTriggersStore = defineStore('insights-triggers', () => {
  const editedTrigger = ref<Trigger | null>(null)
  const triggers = ref<Trigger[]>([])
  const triggerLoading = ref(false)
  // While true, the edit-trigger modal stages into draftTriggers instead of
  // hitting the API; `triggers` stays the committed baseline until Apply.
  const isBatchingTriggers = ref(false)
  const draftTriggers = ref<DraftTrigger[]>([])
  // Apply diffs the draft against this, not the live list: the segment-load
  // fetch is fire-and-forget, so triggers landing mid-batch would otherwise
  // read as staged removals.
  const batchBaselineIds = ref<number[]>([])
  let nextDraftKey = -1

  const manageTrigger = computed(() => window.Silverfin.isCurrentUserAdmin())

  const resolvedTriggers = computed(() => {
    if (useSegmentsStore().isSegmentModified) {
      return []
    }
    return triggers.value
  })

  const manualTriggers = computed(() =>
    resolvedTriggers.value.filter(
      (trigger) => trigger.frequency === NO_REPEAT_TRIGGER_FREQUENCIES.MANUAL
    )
  )

  const oneOffTriggers = computed(() =>
    resolvedTriggers.value.filter(
      (trigger) => trigger.frequency === NO_REPEAT_TRIGGER_FREQUENCIES.ONE_OFF
    )
  )

  const recurringTriggers = computed(() => {
    const noRepeatValues = Object.values(NO_REPEAT_TRIGGER_FREQUENCIES)
    return resolvedTriggers.value.filter(
      (trigger) => !noRepeatValues.includes(trigger.frequency)
    )
  })

  const queryTriggers = computed(() =>
    resolvedTriggers.value.filter(
      (trigger) => trigger.frequency !== NO_REPEAT_TRIGGER_FREQUENCIES.ONE_OFF
    )
  )

  // The batch reads the fetched list, not the display-gated one: a segment
  // modification blanks resolvedTriggers, which would otherwise wipe an open
  // batch's draft and its baseline.
  const batchTriggers = computed(() =>
    triggers.value.filter(
      (trigger) => trigger.frequency !== NO_REPEAT_TRIGGER_FREQUENCIES.ONE_OFF
    )
  )

  function blankTrigger(segmentId: number): Trigger {
    return {
      title: '',
      frequency: 'manual',
      trigger_action: {
        action: 'create_remark_on_company',
        payload: {
          todo: false,
          text: I18n.t('insight_triggers.modal.create_remark.template', {
            example: '{{ text.type }}',
          }),
          tag_ids: [],
        },
      },
      segment: { id: segmentId },
    }
  }

  function setEditedTrigger(trigger: Trigger): void {
    editedTrigger.value = { ...trigger }
  }

  function unsetEditedTrigger(): void {
    editedTrigger.value = null
  }

  function snapshotTriggerBatch(): void {
    draftTriggers.value = cloneDeep(batchTriggers.value).map((trigger) => ({
      ...trigger,
      draftKey: trigger.id as number,
    }))
    batchBaselineIds.value = batchTriggers.value.map(
      (trigger) => trigger.id as number
    )
  }

  function setBatchingTriggers(value: boolean): void {
    // Only the batch's first open snapshots; the edit sub-flow's reopens must
    // not overwrite the draft.
    if (value && !isBatchingTriggers.value) {
      snapshotTriggerBatch()
    }
    isBatchingTriggers.value = value
  }

  function discardTriggerBatch(): void {
    isBatchingTriggers.value = false
    draftTriggers.value = []
    batchBaselineIds.value = []
  }

  // Set comparison, not a joined string: the trigger index has no pinned
  // order, so a refetch can hand back the same entries in another order.
  const batchUntouched = computed(() => {
    if (draftTriggers.value.some((draft) => draft.params)) return false

    const draftKeys = new Set(
      draftTriggers.value.map((draft) => draft.draftKey)
    )
    return (
      draftKeys.size === batchBaselineIds.value.length &&
      batchBaselineIds.value.every((id) => draftKeys.has(id))
    )
  })

  // A late fetch re-snapshots an untouched batch, so the modal doesn't sit on
  // a stale empty list. Once anything is staged, the draft wins.
  watch(batchTriggers, () => {
    if (!isBatchingTriggers.value) return
    if (!batchUntouched.value) return
    snapshotTriggerBatch()
  })

  function stageTriggerSave(trigger: Trigger, params: TriggerSaveParams): void {
    // An edit started outside the manage modal carries no draftKey; its id is
    // the same identity, so it updates its draft instead of appending a copy.
    const draftKey = (trigger as DraftTrigger).draftKey ?? trigger.id
    const staged: DraftTrigger = {
      ...cloneDeep(trigger),
      title: params.title,
      frequency: params.frequency,
      // The edit modal seeds its remark-type and tag state from trigger_action,
      // so a stale payload here would re-stage the pre-edit values on a second
      // edit within the batch.
      trigger_action: {
        ...cloneDeep(trigger.trigger_action),
        action: params.insight_trigger_action_attributes.action,
        payload: cloneDeep(params.insight_trigger_action_attributes.payload),
      },
      draftKey: draftKey ?? nextDraftKey--,
      params,
    }

    const index = draftTriggers.value.findIndex(
      (draft) => draft.draftKey === staged.draftKey
    )
    if (index === -1) {
      draftTriggers.value = [...draftTriggers.value, staged]
    } else {
      draftTriggers.value = draftTriggers.value.map((draft, i) =>
        i === index ? staged : draft
      )
    }
  }

  // Reports whether an entry was actually staged for removal: a missing key
  // (or one from outside this batch) matches nothing, and the caller must not
  // treat that silent no-op as a staged delete.
  function stageTriggerRemoval(draftKey?: number): boolean {
    if (draftKey === undefined) return false

    const remaining = draftTriggers.value.filter(
      (draft) => draft.draftKey !== draftKey
    )
    if (remaining.length === draftTriggers.value.length) return false

    draftTriggers.value = remaining
    return true
  }

  // Cancelled requests (status 0, e.g. the user navigating away) stay silent
  // like jsonWithAlert; every other server failure must be told to the user,
  // because the batch stays open for a retry.
  //
  // A 401 is neither: SFetch answers an expired session by reloading the page
  // to re-authenticate and hands the unparsable body back as a plain parse
  // error, so there is no response to report and nothing the user can act on.
  function alertRequestFailure(error: unknown): void {
    if (!isResponseError(error) || error.status === 0) return

    const errors =
      typeof error.body === 'object' ? error.body.errors : undefined
    const detail = Object.values(errors ?? {}).join()
    alert(detail ? `Something went wrong: ${detail}` : 'Something went wrong')
  }

  // A committed save must not be resent on retry; adopting the created id as
  // both id and draft key makes a later in-batch edit update instead of
  // duplicating, and joining the baseline makes a later removal issue a DELETE
  // for what the create just persisted.
  function commitDraftSave(draftKey: number, savedId?: number): void {
    draftTriggers.value = draftTriggers.value.map((draft) => {
      if (draft.draftKey !== draftKey) return draft

      const id = draft.id ?? savedId
      return { ...draft, id, draftKey: id ?? draft.draftKey, params: undefined }
    })

    if (savedId !== undefined && !batchBaselineIds.value.includes(savedId)) {
      batchBaselineIds.value = [...batchBaselineIds.value, savedId]
    }
  }

  // Requests run sequentially so failures surface one alert at a time.
  // Returns false when anything failed: successes are committed out of the
  // draft, failures stay staged, and the batch stays open for a retry.
  async function applyTriggerBatch(segmentId: number): Promise<boolean> {
    const draftKeys = new Set(draftTriggers.value.map((d) => d.draftKey))
    const removedIds = batchBaselineIds.value.filter((id) => !draftKeys.has(id))
    const staged = draftTriggers.value.filter((draft) => draft.params)
    let failed = false

    // Nothing to send: skip the segment reload, which re-runs the query and
    // re-renders the whole table.
    if (!removedIds.length && !staged.length) {
      discardTriggerBatch()
      return true
    }

    for (const id of removedIds) {
      const url = window.Silverfin.Routes.insight_trigger_path(
        window.Silverfin.currentFirmId,
        id,
        { segment_id: segmentId }
      )
      try {
        await SFetch.json(url, { method: 'DELETE' })
        batchBaselineIds.value = batchBaselineIds.value.filter(
          (baselineId) => baselineId !== id
        )
      } catch (e) {
        failed = true
        alertRequestFailure(e)
      }
    }

    for (const draft of staged) {
      const url = draft.id
        ? window.Silverfin.Routes.insight_trigger_path(
            window.Silverfin.currentFirmId,
            draft.id,
            { segment_id: segmentId }
          )
        : window.Silverfin.Routes.insight_triggers_path(
            window.Silverfin.currentFirmId,
            { segment_id: segmentId }
          )

      let result: { errorMessage?: string; id?: number } | null
      try {
        result = await SFetch.json(url, {
          method: draft.id ? 'PUT' : 'POST',
          body: { insight_trigger: draft.params },
        })
      } catch (e) {
        failed = true
        alertRequestFailure(e)
        continue
      }

      if (result?.errorMessage) {
        failed = true
        alert(result.errorMessage)
        continue
      }

      commitDraftSave(draft.draftKey, result?.id)
    }

    if (failed) {
      await fetchTriggers(segmentId)
      return false
    }

    discardTriggerBatch()
    // loadSegment refreshes the segment-scoped trigger list itself; fetching
    // here as well races two identical requests for the same list.
    void useSegmentsStore().loadSegment({ id: segmentId })
    return true
  }

  async function fetchTriggers(segmentId?: number): Promise<boolean | void> {
    if (!window.Silverfin.isCurrentUserAdmin()) {
      return false
    }

    triggerLoading.value = true
    triggers.value = (await api.fetchTriggers(segmentId)) as Trigger[]
    triggerLoading.value = false
  }

  function runTrigger(trigger: Trigger): void {
    setTriggerRunning({ id: trigger.id as number, running: true })
    void api.runTrigger({
      triggerId: trigger.id as number,
      segmentId: trigger.segment.id,
    })
  }

  function setTriggerRunning(runningStatus: TriggerRunningStatus): void {
    triggers.value = triggers.value.map((trigger) => {
      if (trigger.id === runningStatus.id) {
        return {
          ...trigger,
          running: runningStatus.running,
          id: runningStatus.id,
          total_count: runningStatus.total_count,
          processed_ids: runningStatus.processed_ids,
          finished: runningStatus.finished,
        }
      }
      return trigger
    })
  }

  return {
    editedTrigger,
    triggers,
    triggerLoading,
    isBatchingTriggers,
    draftTriggers,
    batchBaselineIds,
    manageTrigger,
    resolvedTriggers,
    manualTriggers,
    oneOffTriggers,
    recurringTriggers,
    queryTriggers,
    blankTrigger,
    setEditedTrigger,
    unsetEditedTrigger,
    setBatchingTriggers,
    discardTriggerBatch,
    stageTriggerSave,
    stageTriggerRemoval,
    applyTriggerBatch,
    fetchTriggers,
    runTrigger,
    setTriggerRunning,
  }
})
